// SHADOW MARKET™ — Product card aligned with the main REBEL aesthetic.
// Inter font, orange-amber CTA, glass dark, subtle borders. Fully i18n.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Check, Sparkles, Flame } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { useShopI18n, typeLabelSingular } from "../i18n/shop";

export const ShopProductCard = ({ product, indexInRow = 0 }) => {
  const { addItem } = useCart();
  const { isPro } = useAuth();
  const { lang } = useLanguage();
  const t = useShopI18n(lang);
  const [selectedSize, setSelectedSize] = useState(null);
  const [added, setAdded] = useState(false);
  const isOut = (s) => product.soldOutSizes?.includes(s);
  const canCustomize = product.type === "polo" || product.type === "camiseta";

  const handleAdd = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const size = selectedSize || product.sizes.find((s) => !isOut(s));
    if (!size) return;
    addItem(product, size, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <div
      className="shop-card shop-card-hover relative flex flex-col bg-zinc-950/60 backdrop-blur-sm"
      data-testid={`shop-card-${product.id}`}
    >
      {/* IMAGE — placeholder while real photos are shot */}
      <Link
        to={`/shadow-market/${product.slug}`}
        className="relative block aspect-[3/4] overflow-hidden border-b border-white/[0.06]"
        style={{
          background:
            "radial-gradient(circle at 50% 40%, rgba(255,140,0,0.05), transparent 60%), #0a0a12",
        }}
      >
        {/* Subtle big watermark — Bitcoin ₿ */}
        <div
          className="absolute inset-0 flex items-center justify-center font-black tracking-tighter"
          style={{
            color: "rgba(255,140,0,0.04)",
            fontSize: "clamp(5rem, 12vw, 9rem)",
          }}
          aria-hidden="true"
        >
          ₿
        </div>

        {/* RE₿EL logo + color swatch */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
          <div className="text-base font-black tracking-tight text-white/30">
            RE<span className="text-orange-400/60">₿</span>EL™
          </div>
          <div
            className="h-14 w-14 rounded-xl"
            style={{
              background: product.colorHex,
              boxShadow: "0 0 0 1px rgba(255,255,255,0.12), 0 12px 32px rgba(0,0,0,0.5)",
            }}
            title={product.color}
          />
          <div className="text-[9px] font-bold uppercase tracking-[0.24em] text-white/40">
            {product.color}
          </div>
        </div>

        {/* Badge R₿L — 25% dto pagando con $R₿L (aditivo, no toca el diseño) */}
        <span
          className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.12em]"
          style={{
            background: "rgba(249,115,22,0.16)",
            color: "#fb923c",
            border: "1px solid rgba(249,115,22,0.45)",
            backdropFilter: "blur(6px)",
          }}
          data-testid={`shop-rbl-badge-${product.id}`}
        >
          <Flame className="h-2.5 w-2.5" /> {t.rbl_badge_discount}
        </span>

        {product.badge && (
          <span
            className="absolute left-3 top-3 rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.16em]"
            style={{
              background: product.badgeBg || "transparent",
              color: product.badgeText || "#fff",
              border: product.badgeBorder ? `1px solid ${product.badgeBorder}` : "1px solid rgba(255,255,255,0.1)",
            }}
          >
            {product.badge}
          </span>
        )}

        {/* Quick add — reveals on hover */}
        <button
          onClick={handleAdd}
          className="shop-quick-add absolute inset-x-3 bottom-3 inline-flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2 text-[10px] font-black uppercase tracking-[0.14em] text-black shadow-[0_8px_24px_rgba(255,140,0,0.45)]"
          data-testid={`shop-quickadd-${product.id}`}
        >
          {added ? <><Check className="h-3 w-3" /> {t.card_added}</> : <><Plus className="h-3 w-3" /> {t.card_quick_add}</>}
        </button>
      </Link>

      {/* INFO */}
      <div className="flex flex-col gap-2.5 p-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-orange-300/80">
          {typeLabelSingular(lang, product.type)}
        </div>

        <Link to={`/shadow-market/${product.slug}`}>
          <h3 className="text-base font-black tracking-tight text-white transition-colors hover:text-orange-200">
            {product.name}
          </h3>
        </Link>

        <div className="text-xs text-white/45">{product.color}</div>

        {/* Sizes */}
        <div className="mt-1 flex flex-wrap gap-1.5">
          {product.sizes.map((s) => {
            const out = isOut(s);
            const active = selectedSize === s;
            return (
              <button
                key={s}
                disabled={out}
                onClick={() => !out && setSelectedSize(s)}
                className="rounded-full px-2.5 py-1 text-[10px] font-bold tracking-[0.08em] transition-all"
                style={{
                  border: `1px solid ${active ? "rgba(255,140,0,0.6)" : "rgba(255,255,255,0.12)"}`,
                  color: out ? "rgba(255,255,255,0.25)" : active ? "#fbbf24" : "rgba(255,255,255,0.55)",
                  background: active ? "rgba(255,140,0,0.10)" : "transparent",
                  textDecoration: out ? "line-through" : "none",
                  cursor: out ? "not-allowed" : "pointer",
                  opacity: out ? 0.4 : 1,
                }}
                data-testid={`shop-size-${product.id}-${s}`}
              >
                {s}
              </button>
            );
          })}
        </div>

        {/* Price + CTA */}
        <div className="mt-2 flex items-end justify-between gap-3">
          <div>
            <div className="text-lg font-black text-white">
              €{(isPro ? product.proPrice : product.price).toFixed(2)}
            </div>
            {isPro ? (
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-orange-300">
                {t.card_pro_label} −15%
              </div>
            ) : (
              <div className="text-[10px] tracking-[0.06em] text-white/35">
                {t.card_pro_hint(product.proPrice)}
              </div>
            )}
          </div>
          <button
            onClick={handleAdd}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.14em] transition-all ${
              added
                ? "bg-emerald-500 text-black"
                : "border border-orange-400/50 bg-transparent text-orange-300 hover:-translate-y-0.5 hover:border-orange-300 hover:bg-orange-400/10 hover:text-orange-200"
            }`}
            data-testid={`shop-add-${product.id}`}
          >
            {added ? <><Check className="h-3 w-3" /> {t.card_added}</> : <><Plus className="h-3 w-3" /> {t.card_add}</>}
          </button>
        </div>

        {canCustomize && (
          <Link
            to={`/shadow-market/personalizar/${product.slug}`}
            className="studio-customize-cta mt-2 self-start"
            data-testid={`shop-customize-${product.id}`}
          >
            <Sparkles className="h-3 w-3" />
            {t.customize_cta} +€15
          </Link>
        )}
      </div>
    </div>
  );
};

export default ShopProductCard;
